import { Box } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, GRID_FONT, GRID_RADIUS, GRID_TINT, GRID_Z, NEUTRAL } from "../../../../theme/tokens";
import { formatWeekRangeWithMonth } from "../../../../utils/dateFormatting";
import { LEADING_TRACKS_COVERAGE, LEADING_TRACKS_PEOPLE } from "../gridGeometry";
import { StickyLeadingCell } from "./StickyLeadingCell";

type Props = {
	weeks: number;
	/** First day of week 1 (cycle anchor) — drives the per-week date range. */
	cycleStart: DateTime;
	/**
	 * Which of the `weeks` columns contains today, or `-1` when none does. Passed in rather than assumed
	 * to be column 0: the People roster can page its projection window off the present, and naming a
	 * November week "Current" (or giving it the active tint) would be a plain falsehood. The Coverage
	 * grids are always anchored on today's week and pass `0`.
	 */
	activeWeekIndex: number;
	/**
	 * Look of the row. `coverage` = the three-part cell (Week N · date range ·
	 * active/upcoming chip) with a brand top-rule on the active week, carrying the
	 * `.ms3-week-header` hover-tint hook. `people` = the flatter single-line cell
	 * ("Current (range)", "Next (range)", else the range alone) with no chip and no hover wiring — kept EXACTLY as
	 * People renders today (different py / font / no split), surfaced as this variant.
	 */
	variant: "coverage" | "people";
	/** Test-id root: `${testIdPrefix}-week-header-${w}` (e.g. `ms3`, `ms3-detail`, `ms3-people`). */
	testIdPrefix: string;
	/**
	 * People's normalized view: narrow day columns, so the label drops the active/upcoming word and
	 * shrinks. The active week is still distinguishable — its brand tint is unchanged — and the label
	 * ellipsizes rather than bleeding into the next week if a translation still runs long.
	 */
	compact?: boolean;
};

/** Row 1 of every v3 grid: the empty frozen corner + one header cell per week. */
export const GridWeekHeaderRow = ({ weeks, cycleStart, activeWeekIndex, variant, testIdPrefix, compact = false }: Props) => {
	const { t } = useTranslation();
	const isCoverage = variant === "coverage";
	return (
		// `display: contents` carries the ARIA row without adding a box to the CSS grid.
		<Box role="row" sx={{ display: "contents" }}>
			{/* Frozen corner spans the axis's whole leading zone — three tracks on each. */}
			<StickyLeadingCell
				left={0}
				variant="header"
				role="presentation"
				showEdgeShadow
				sx={{ gridColumn: `1 / ${(isCoverage ? LEADING_TRACKS_COVERAGE : LEADING_TRACKS_PEOPLE) + 1}`, top: 0 }}
			/>
			{Array.from({ length: weeks }, (_, w) => {
				const start = cycleStart.plus({ days: w * 7 });
				const isActive = w === activeWeekIndex;
				// "Current" and "Next" name the two weeks people act on. Every other week carries no ordinal:
				// "Week 3" of a paged, rotation-anchored window means nothing to the reader, so its date range
				// alone locates it. Measured from the ACTIVE week, so a window paged off the present shows
				// ranges only.
				const weeksFromToday = activeWeekIndex === -1 ? null : w - activeWeekIndex;
				const weekLabel = weeksFromToday === 0 ? t("schedule.template.v3.week.current") : weeksFromToday === 1 ? t("schedule.template.v3.week.next") : null;
				const range = formatWeekRangeWithMonth(start, start.plus({ days: 6 }));
				const peopleLabel = weekLabel === null ? range : compact ? `${weekLabel} · ${range}` : `${weekLabel} (${range})`;
				return (
					<Fragment key={start.toISODate()}>
						{isCoverage ? (
							<Box
								className="ms3-week-header"
								role="columnheader"
								data-week={w}
								data-testid={`${testIdPrefix}-week-header-${w}`}
								sx={(theme) => ({
									gridColumn: "span 7",
									position: "sticky",
									top: 0,
									zIndex: GRID_Z.WEEK_HEADER,
									display: "flex",
									alignItems: "baseline",
									justifyContent: "center",
									gap: 0.75,
									py: 0.875,
									backgroundColor: lighten(theme.palette.primary.light, isActive ? GRID_TINT.ACTIVE_WEEK : GRID_TINT.UPCOMING_WEEK),
									// The active (current) week gets a brand top-rule so "now" reads at a glance.
									borderTop: isActive ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
									color: theme.palette.primary.dark,
									whiteSpace: "nowrap",
								})}
							>
								<Box component="span" sx={{ fontWeight: 800, fontSize: GRID_FONT.BASE, letterSpacing: "0.02em" }}>
									{t("week")} {w + 1}
								</Box>
								<Box component="span" sx={{ fontSize: GRID_FONT.SM, fontWeight: 600, opacity: ALPHA.MUTED_SECONDARY }}>
									{range}
								</Box>
								<Box
									component="span"
									sx={(theme) => ({
										fontSize: GRID_FONT.MICRO,
										fontWeight: 800,
										textTransform: "uppercase",
										letterSpacing: GRID_FONT.LETTER_SPACING.WIDE,
										px: 0.625,
										py: "1px",
										borderRadius: GRID_RADIUS.PANEL,
										color: isActive ? theme.palette.primary.contrastText : theme.palette.primary.dark,
										backgroundColor: isActive ? theme.palette.primary.main : alpha(theme.palette.primary.main, GRID_TINT.ACTIVE_BADGE_ALPHA),
									})}
								>
									{t(isActive ? "active" : "upcoming")}
								</Box>
							</Box>
						) : (
							<Box
								role="columnheader"
								data-week={w}
								data-testid={`${testIdPrefix}-week-header-${w}`}
								sx={(theme) => ({
									gridColumn: "span 7",
									position: "sticky",
									top: 0,
									zIndex: GRID_Z.WEEK_HEADER,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									py: 0.75,
									px: compact ? 0.5 : 0,
									backgroundColor: lighten(theme.palette.primary.light, isActive ? GRID_TINT.ACTIVE_WEEK : GRID_TINT.UPCOMING_WEEK),
									color: theme.palette.primary.dark,
									fontWeight: 700,
									// Type stays the same size in both views — the compact header is shorter, not smaller;
									// the day columns are widened to fit it rather than the label being shrunk to squeeze in.
									fontSize: GRID_FONT.BASE,
									textTransform: "uppercase",
									letterSpacing: "0.03em",
									whiteSpace: "nowrap",
									// The label spans the week's 7 tracks; without these it bleeds over the next week
									// instead of being clipped, which is what made the headers look overlapped.
									minWidth: 0,
									overflow: "hidden",
									textOverflow: "ellipsis",
								})}
							>
								{peopleLabel}
							</Box>
						)}
						{w < weeks - 1 && <Box role="presentation" sx={{ backgroundColor: NEUTRAL[300] }} />}
					</Fragment>
				);
			})}
		</Box>
	);
};
